import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { http, money, formatErr } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { TID } from "@/lib/testIds";
import { CheckCircle2, ChevronRight, ChevronLeft, CreditCard, Banknote, Wallet, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { openRazorpay } from "@/lib/razorpay";

const STEPS = ["Contact", "Address", "Payment"];

const PAYMENTS = [
  { id: "cod", label: "Cash on Delivery", desc: "Pay when it arrives", icon: Banknote },
  { id: "stripe", label: "Card / UPI (Stripe)", desc: "Cards, Apple Pay, Google Pay", icon: CreditCard },
  { id: "razorpay", label: "Razorpay", desc: "UPI, NetBanking, Wallets, Cards", icon: Wallet },
  { id: "phonepe", label: "PhonePe", desc: "PhonePe UPI checkout", icon: Smartphone },
  { id: "paypal", label: "PayPal", desc: "Pay with PayPal", icon: Wallet },
];

export default function Checkout() {
  const nav = useNavigate();
  const { items, totals } = useCart();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [enabled, setEnabled] = useState({ cod: true, stripe: true });
  const [contact, setContact] = useState({ email: "" });
  const [addr, setAddr] = useState({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "India" });
  const [method, setMethod] = useState("cod");

  useEffect(() => {
    http.get("/store/info").then((r) => setEnabled(r.data.payment_methods));
  }, []);

  useEffect(() => {
    if (items.length === 0) nav("/cart");
  }, [items.length, nav]);

  const canNext = () => {
    if (step === 0) return /\S+@\S+\.\S+/.test(contact.email);
    if (step === 1) return addr.full_name && addr.phone && addr.line1 && addr.city && addr.state && addr.postal_code;
    return true;
  };

  const place = async () => {
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        email: contact.email,
        address: addr,
        payment_method: method,
        notes: "",
      };
      const { data } = await http.post("/checkout/place", payload);
      if (method === "stripe") {
        // Create stripe session
        const { data: s } = await http.post("/payments/stripe/session", {
          order_id: data.order_id,
          origin_url: window.location.origin,
        });
        window.location.href = s.url;
        return;
      }
      if (method === "razorpay") {
        // Create Razorpay order then open checkout
        const { data: rzp } = await http.post("/payments/razorpay/order", { order_id: data.order_id });
        await openRazorpay(rzp, {
          orderId: data.order_id,
          name: addr.full_name,
          email: contact.email,
          phone: addr.phone,
          onSuccess: () => nav(`/order/success?order_id=${data.order_id}`),
          onDismiss: () => toast.info("Payment cancelled"),
        });
        return;
      }
      if (method === "cod") {
        nav(`/order/success?order_id=${data.order_id}`);
        return;
      }
      // Other gateways not yet configured
      toast.info(`${method.toUpperCase()} requires API keys in admin → Settings. Order placed as pending.`);
      nav(`/order/success?order_id=${data.order_id}`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bls-container py-12 grid lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2">
        <h1 className="font-display text-4xl font-bold tracking-tighter">Checkout</h1>

        {/* Stepper */}
        <div className="mt-8 flex items-center gap-2">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className={`flex items-center gap-2 ${i <= step ? "text-[#16161f]" : "text-black/35"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < step ? "bg-[#7f77dd] text-white" : i === step ? "bg-[#16161f] text-white" : "bg-black/10 text-black/40"}`}>
                  {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <span className="text-sm font-medium hidden sm:inline">{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-[#7f77dd]" : "bg-black/10"}`} />}
            </div>
          ))}
        </div>

        <div className="mt-10 bg-white rounded-2xl border border-black/5 p-8">
          {step === 0 && (
            <div className="space-y-4 max-w-md bls-fade-up">
              <h2 className="font-display text-2xl font-bold">Your contact</h2>
              <label className="block">
                <span className="text-sm font-medium text-black/65">Email</span>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="bls-input mt-1"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  data-testid="checkout-email"
                />
              </label>
              <p className="text-xs text-black/50">We&apos;ll send your order confirmation to this email.</p>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4 bls-fade-up">
              <h2 className="font-display text-2xl font-bold">Shipping address</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" testid="checkout-name" name="full_name" autoComplete="name" value={addr.full_name} onChange={(v) => setAddr({ ...addr, full_name: v })} />
                <Field label="Phone" testid="checkout-phone" name="phone" autoComplete="tel" value={addr.phone} onChange={(v) => setAddr({ ...addr, phone: v })} />
                <Field label="Address line 1" testid="checkout-line1" name="line1" autoComplete="address-line1" value={addr.line1} onChange={(v) => setAddr({ ...addr, line1: v })} className="sm:col-span-2" />
                <Field label="Address line 2 (optional)" testid="checkout-line2" name="line2" autoComplete="address-line2" value={addr.line2} onChange={(v) => setAddr({ ...addr, line2: v })} className="sm:col-span-2" />
                <Field label="City" testid="checkout-city" name="city" autoComplete="address-level2" value={addr.city} onChange={(v) => setAddr({ ...addr, city: v })} />
                <Field label="State" testid="checkout-state" name="state" autoComplete="address-level1" value={addr.state} onChange={(v) => setAddr({ ...addr, state: v })} />
                <Field label="Postal code" testid="checkout-postal" name="postal_code" autoComplete="postal-code" value={addr.postal_code} onChange={(v) => setAddr({ ...addr, postal_code: v })} />
                <Field label="Country" testid="checkout-country" name="country" autoComplete="country-name" value={addr.country} onChange={(v) => setAddr({ ...addr, country: v })} />
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4 bls-fade-up">
              <h2 className="font-display text-2xl font-bold">Payment method</h2>
              <div className="space-y-3">
                {PAYMENTS.map((p) => {
                  const isEnabled = enabled[p.id];
                  const Icon = p.icon;
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center gap-4 p-4 border rounded-xl cursor-pointer transition-all ${method === p.id ? "border-[#7f77dd] bg-[#e8e6fb]/30" : "border-black/10"} ${!isEnabled ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`${TID.checkoutPaymentOption}-${p.id}`}
                    >
                      <input
                        type="radio"
                        name="pm"
                        value={p.id}
                        checked={method === p.id}
                        disabled={!isEnabled}
                        onChange={() => setMethod(p.id)}
                        className="w-4 h-4 accent-[#7f77dd]"
                      />
                      <div className="w-10 h-10 rounded-lg bg-[#16161f] text-white inline-flex items-center justify-center">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold flex items-center gap-2">
                          {p.label}
                          {!isEnabled && <span className="bls-tag" style={{ background: "#0001", color: "#0007" }}>Coming soon</span>}
                        </div>
                        <div className="text-xs text-black/55">{p.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={() => (step === 0 ? nav("/cart") : setStep(step - 1))}
            className="bls-btn-secondary"
            data-testid={TID.checkoutBack}
          >
            <ChevronLeft className="w-4 h-4" /> {step === 0 ? "Back to cart" : "Back"}
          </button>
          {step < 2 ? (
            <button
              onClick={() => canNext() && setStep(step + 1)}
              disabled={!canNext()}
              className="bls-btn-primary disabled:opacity-40"
              data-testid={TID.checkoutNext}
            >
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={place}
              disabled={submitting || !enabled[method]}
              className="bls-btn-primary disabled:opacity-40"
              data-testid={TID.checkoutPlaceOrder}
            >
              {submitting ? "Placing…" : method === "stripe" ? "Pay with Stripe" : method === "cod" ? "Place order (COD)" : "Place order"}
            </button>
          )}
        </div>
      </div>

      <aside className="bg-white rounded-2xl border border-black/5 p-6 h-fit">
        <h3 className="font-display font-bold text-lg">In your cart</h3>
        <div className="mt-4 space-y-3">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-3 items-center text-sm">
              <div className="bls-thumb w-12 h-12 flex-shrink-0">
                {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{it.name}</p>
                <p className="text-black/50">× {it.quantity}</p>
              </div>
              <p className="font-semibold">{money(it.price * it.quantity, totals.currency_symbol)}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 space-y-2 text-sm border-t border-black/5 pt-4">
          <Row label="Subtotal" v={money(totals.subtotal, totals.currency_symbol)} />
          <Row label="Shipping" v={totals.shipping > 0 ? money(totals.shipping, totals.currency_symbol) : "Free"} />
          {totals.tax > 0 && <Row label="Tax" v={money(totals.tax, totals.currency_symbol)} />}
        </div>
        <div className="mt-4 pt-4 border-t border-black/10 flex justify-between items-baseline">
          <span className="font-display font-bold">Total</span>
          <span className="font-display text-2xl font-bold">{money(totals.total, totals.currency_symbol)}</span>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, value, onChange, testid, className = "", name = "", autoComplete = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-sm font-medium text-black/65">{label}</span>
      <input
        className="bls-input mt-1"
        value={value}
        name={name}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </label>
  );
}

function Row({ label, v }) {
  return <div className="flex justify-between"><span className="text-black/55">{label}</span><span>{v}</span></div>;
}
